import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Download, BarChart3, TrendingUp, AlertTriangle, Phone, Mail, Globe, Shield, Wrench, Package } from 'lucide-react';
import { toast } from 'sonner';

function StatCard({ title, value, sub, icon: Icon, color = "text-primary" }) {
  return (
    <Card className="border border-border">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          {Icon && <Icon size={20} className={color} />}
        </div>
      </CardContent>
    </Card>
  );
}

function DistBar({ data, color = 'bg-primary' }) {
  const max = Math.max(...Object.values(data), 1);
  if (Object.keys(data).length === 0) return <p className="text-sm text-muted-foreground py-2">No data</p>;
  return (
    <div className="space-y-2">
      {Object.entries(data).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
        <div key={k} className="flex items-center gap-2">
          <span className="text-xs w-24 text-right truncate text-muted-foreground">{k}</span>
          <div className="flex-1 h-5 bg-muted rounded-sm overflow-hidden">
            <div className={`h-full ${color} rounded-sm transition-all`} style={{ width: `${(v / max) * 100}%` }} />
          </div>
          <span className="text-xs font-medium w-8">{v}</span>
        </div>
      ))}
    </div>
  );
}

export default function ReportsPage() {
  const [dash, setDash] = useState(null);
  const [weekly, setWeekly] = useState([]);
  const [invReport, setInvReport] = useState(null);

  useEffect(() => {
    api.get('/reports/dashboard').then(r => setDash(r.data));
    api.get('/reports/weekly-support').then(r => setWeekly(r.data.weeks || []));
    api.get('/reports/inventory-report').then(r => setInvReport(r.data));
  }, []);

  const exportCSV = (type) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/export/${type}`;
    window.open(url, '_blank');
    toast.success(`${type} export started`);
  };

  const t = dash?.tickets || {};
  const inv = dash?.inventory || {};
  const opp = dash?.opportunities || {};

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-bold tracking-tight">Reports & Analytics</h1><p className="text-sm text-muted-foreground mt-1">Comprehensive business intelligence</p></div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => exportCSV('tickets')} data-testid="export-tickets"><Download size={14} className="mr-1" /> Tickets</Button>
          <Button variant="outline" size="sm" onClick={() => exportCSV('opportunities')} data-testid="export-opportunities"><Download size={14} className="mr-1" /> Opportunities</Button>
          <Button variant="outline" size="sm" onClick={() => exportCSV('defects')} data-testid="export-defects"><Download size={14} className="mr-1" /> Defects</Button>
          <Button variant="outline" size="sm" onClick={() => exportCSV('onsite')} data-testid="export-onsite"><Download size={14} className="mr-1" /> Onsite</Button>
          <Button variant="outline" size="sm" onClick={() => exportCSV('inventory')} data-testid="export-inventory"><Download size={14} className="mr-1" /> Inventory</Button>
        </div>
      </div>

      {!dash ? <p className="text-muted-foreground">Loading...</p> : (
        <Tabs defaultValue="support">
          <TabsList className="flex-wrap h-auto gap-1">
            <TabsTrigger value="support" data-testid="tab-support"><Phone size={14} className="mr-1" /> Support</TabsTrigger>
            <TabsTrigger value="inventory" data-testid="tab-inventory"><Package size={14} className="mr-1" /> Inventory</TabsTrigger>
            <TabsTrigger value="sales" data-testid="tab-sales"><TrendingUp size={14} className="mr-1" /> Sales</TabsTrigger>
            <TabsTrigger value="trends" data-testid="tab-trends"><BarChart3 size={14} className="mr-1" /> Trends</TabsTrigger>
          </TabsList>

          {/* ═══ SUPPORT TAB ═══ */}
          <TabsContent value="support" className="mt-4 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard title="Total Tickets" value={t.total} icon={BarChart3} />
              <StatCard title="SLA Compliance" value={`${t.sla_compliance}%`} icon={Shield} color="text-emerald-600" />
              <StatCard title="Escalated" value={t.escalated_count} icon={AlertTriangle} color="text-destructive" />
              <StatCard title="Remote Resolved" value={t.resolved_remote} icon={Wrench} color="text-blue-600" sub={`${((t.resolved_remote / Math.max(t.total, 1)) * 100).toFixed(0)}% of total`} />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Card className="border border-border"><CardHeader className="pb-2"><CardTitle className="text-sm">By Source</CardTitle></CardHeader>
                <CardContent><DistBar data={t.source_distribution || {}} color="bg-blue-500" /></CardContent>
              </Card>
              <Card className="border border-border"><CardHeader className="pb-2"><CardTitle className="text-sm">By Issue Type</CardTitle></CardHeader>
                <CardContent><DistBar data={t.issue_type_distribution || {}} color="bg-violet-500" /></CardContent>
              </Card>
              <Card className="border border-border"><CardHeader className="pb-2"><CardTitle className="text-sm">By Retailer</CardTitle></CardHeader>
                <CardContent><DistBar data={t.retailer_distribution || {}} color="bg-amber-500" /></CardContent>
              </Card>
              <Card className="border border-border"><CardHeader className="pb-2"><CardTitle className="text-sm">By Agency</CardTitle></CardHeader>
                <CardContent><DistBar data={t.agency_distribution || {}} color="bg-teal-500" /></CardContent>
              </Card>
              <Card className="border border-border"><CardHeader className="pb-2"><CardTitle className="text-sm">Escalation Reasons</CardTitle></CardHeader>
                <CardContent><DistBar data={t.escalation_reasons || {}} color="bg-red-500" /></CardContent>
              </Card>
              <Card className="border border-border"><CardHeader className="pb-2"><CardTitle className="text-sm">Priority Distribution</CardTitle></CardHeader>
                <CardContent><DistBar data={t.priority_distribution || {}} color="bg-orange-500" /></CardContent>
              </Card>
            </div>

            {/* Weekly Support Table */}
            <Card className="border border-border">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Weekly Support Summary</CardTitle></CardHeader>
              <CardContent>
                <div className="rounded-md border border-border">
                  <Table>
                    <TableHeader><TableRow className="hover:bg-transparent">
                      <TableHead>Week</TableHead><TableHead>Calls</TableHead><TableHead>Emails</TableHead><TableHead>Portal</TableHead><TableHead>Total</TableHead><TableHead>Resolved</TableHead><TableHead>Escalated</TableHead><TableHead>Past SLA</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {weekly.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-4 text-muted-foreground">No data yet</TableCell></TableRow>}
                      {weekly.map((w, i) => (
                        <TableRow key={i} className="hover:bg-muted/50">
                          <TableCell className="text-xs font-mono">{w.week_start}</TableCell>
                          <TableCell>{w.calls}</TableCell>
                          <TableCell>{w.emails}</TableCell>
                          <TableCell>{w.portal}</TableCell>
                          <TableCell className="font-semibold">{w.total_received}</TableCell>
                          <TableCell><span className="text-emerald-600 font-medium">{w.resolved}</span></TableCell>
                          <TableCell>{w.escalated > 0 ? <Badge variant="secondary" className="text-xs bg-red-500/10 text-red-700">{w.escalated}</Badge> : '0'}</TableCell>
                          <TableCell>{w.past_sla_open > 0 ? <Badge variant="secondary" className="text-xs bg-amber-500/10 text-amber-700">{w.past_sla_open}</Badge> : '0'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ INVENTORY TAB ═══ */}
          <TabsContent value="inventory" className="mt-4 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard title="Products" value={inv.total_products} icon={Package} />
              <StatCard title="Total Assets" value={inv.total_assets} />
              <StatCard title="Available" value={inv.available_assets} color="text-emerald-600" />
              <StatCard title="Deployed" value={inv.deployed_assets} color="text-blue-600" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <StatCard title="Defects" value={dash.defects?.total || 0} sub={`${dash.defects?.chronic || 0} chronic`} icon={AlertTriangle} color="text-destructive" />
              <StatCard title="Onsite Requests" value={dash.onsite?.total || 0} sub={`${dash.onsite?.completed || 0} completed`} icon={Wrench} color="text-violet-600" />
            </div>

            {inv.low_stock_products?.length > 0 && (
              <Card className="border border-destructive/30">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-destructive">Low Stock Alert</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    {inv.low_stock_products.map(p => <div key={p.product_id} className="text-sm flex justify-between"><span>{p.name}</span><Badge variant="secondary" className="text-xs bg-red-500/10 text-red-700">Stock: {p.stock_level}</Badge></div>)}
                  </div>
                </CardContent>
              </Card>
            )}

            {invReport && (
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="border border-border"><CardHeader className="pb-2"><CardTitle className="text-sm">Parts Used This Week</CardTitle></CardHeader>
                  <CardContent>{invReport.parts_weekly?.length > 0 ? <DistBar data={Object.fromEntries(invReport.parts_weekly.map(p => [p.name || p.product_id, p.quantity]))} color="bg-blue-500" /> : <p className="text-sm text-muted-foreground">No parts used this week</p>}</CardContent>
                </Card>
                <Card className="border border-border"><CardHeader className="pb-2"><CardTitle className="text-sm">Parts Used YTD</CardTitle></CardHeader>
                  <CardContent>{invReport.parts_ytd?.length > 0 ? <DistBar data={Object.fromEntries(invReport.parts_ytd.map(p => [p.name || p.product_id, p.quantity]))} color="bg-emerald-500" /> : <p className="text-sm text-muted-foreground">No parts data</p>}</CardContent>
                </Card>
                <Card className="border border-border"><CardHeader className="pb-2"><CardTitle className="text-sm">Issues by Type</CardTitle></CardHeader>
                  <CardContent><DistBar data={invReport.issues_by_type || {}} color="bg-violet-500" /></CardContent>
                </Card>
                <Card className="border border-border"><CardHeader className="pb-2"><CardTitle className="text-sm">Issues by Retailer</CardTitle></CardHeader>
                  <CardContent><DistBar data={invReport.issues_by_retailer || {}} color="bg-amber-500" /></CardContent>
                </Card>
              </div>
            )}
            {invReport && (
              <Card className="border border-border"><CardHeader className="pb-2"><CardTitle className="text-sm">Remote Resolution Rate</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="text-3xl font-bold text-emerald-600">{invReport.remote_rate}%</div>
                    <p className="text-sm text-muted-foreground">{invReport.remote_resolved} of {invReport.total_resolved} resolved tickets were handled remotely (phone/chat/email)</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ═══ SALES TAB ═══ */}
          <TabsContent value="sales" className="mt-4 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard title="Total Opportunities" value={opp.total} icon={TrendingUp} />
              <StatCard title="Pipeline Value" value={`$${(opp.pipeline_value || 0).toLocaleString()}`} color="text-blue-600" />
              <StatCard title="Won" value={opp.won} sub={`$${(opp.won_value || 0).toLocaleString()}`} color="text-emerald-600" />
              <StatCard title="Conversion Rate" value={`${opp.conversion_rate}%`} />
            </div>
            <Card className="border border-border"><CardHeader className="pb-2"><CardTitle className="text-sm">Pipeline Stage Distribution</CardTitle></CardHeader>
              <CardContent><DistBar data={opp.stage_distribution || {}} color="bg-primary" /></CardContent>
            </Card>
          </TabsContent>

          {/* ═══ TRENDS TAB ═══ */}
          <TabsContent value="trends" className="mt-4 space-y-6">
            <Card className="border border-border">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Weekly Trend Analysis</CardTitle></CardHeader>
              <CardContent>
                {weekly.length >= 2 ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-4 gap-3 text-center text-sm">
                      <div><p className="text-muted-foreground">Volume Trend</p><p className={`font-bold text-lg ${weekly[0].total_received >= weekly[1].total_received ? 'text-red-600' : 'text-emerald-600'}`}>{weekly[0].total_received >= weekly[1].total_received ? '+' : ''}{weekly[0].total_received - weekly[1].total_received} WoW</p></div>
                      <div><p className="text-muted-foreground">Resolution Trend</p><p className={`font-bold text-lg ${weekly[0].resolved >= weekly[1].resolved ? 'text-emerald-600' : 'text-red-600'}`}>{weekly[0].resolved >= weekly[1].resolved ? '+' : ''}{weekly[0].resolved - weekly[1].resolved} WoW</p></div>
                      <div><p className="text-muted-foreground">Escalation Trend</p><p className={`font-bold text-lg ${weekly[0].escalated <= weekly[1].escalated ? 'text-emerald-600' : 'text-red-600'}`}>{weekly[0].escalated > weekly[1].escalated ? '+' : ''}{weekly[0].escalated - weekly[1].escalated} WoW</p></div>
                      <div><p className="text-muted-foreground">SLA Breach Trend</p><p className={`font-bold text-lg ${weekly[0].past_sla_open <= weekly[1].past_sla_open ? 'text-emerald-600' : 'text-red-600'}`}>{weekly[0].past_sla_open > weekly[1].past_sla_open ? '+' : ''}{weekly[0].past_sla_open - weekly[1].past_sla_open} WoW</p></div>
                    </div>
                    {dash?.defects?.chronic > 0 && <div className="p-3 rounded-md bg-destructive/5 border border-destructive/20"><p className="text-sm"><AlertTriangle size={14} className="inline mr-1 text-destructive" /> <strong>{dash.defects.chronic}</strong> chronic defects detected. High-frequency issues may indicate systemic problems.</p></div>}
                    {t.software_delayed > 0 && <div className="p-3 rounded-md bg-amber-500/5 border border-amber-500/20"><p className="text-sm"><AlertTriangle size={14} className="inline mr-1 text-amber-600" /> <strong>{t.software_delayed}</strong> tickets flagged with software delays.</p></div>}
                  </div>
                ) : <p className="text-sm text-muted-foreground">Need at least 2 weeks of data for trend analysis</p>}
              </CardContent>
            </Card>
            {invReport && Object.keys(invReport.issues_by_agency || {}).length > 0 && (
              <Card className="border border-border"><CardHeader className="pb-2"><CardTitle className="text-sm">Issues by Agency</CardTitle></CardHeader>
                <CardContent><DistBar data={invReport.issues_by_agency || {}} color="bg-teal-500" /></CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
